import type { QuotaQuery } from '@pique/graphql';
import {
  backoffRetry,
  catchErrorInto,
  effect,
  Entity,
  exhaustMapSwitchUntilChanged,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { EMPTY, map, mergeMap } from 'rxjs';

import { isBackendError, isNetworkError } from '../error';
import type { AuthService } from '../services/auth';
import type { UserQuotaStore } from '../stores/user-quota';

export class UserQuota extends Entity {
  quota$ = new LiveData<NonNullable<QuotaQuery['currentUser']>['quota']>(null);

  isRevalidating$ = new LiveData(false);
  error$ = new LiveData<any | null>(null);

  constructor(
    private readonly authService: AuthService,
    private readonly store: UserQuotaStore
  ) {
    super();
  }

  revalidate = effect(
    map(() => ({
      accountId: this.authService.session.account$.value?.id,
    })),
    exhaustMapSwitchUntilChanged(
      (a: { accountId?: string }, b: { accountId?: string }) => a.accountId === b.accountId,
      ({ accountId }: { accountId?: string }) =>
        fromPromise(async (signal: AbortSignal) => {
          if (!accountId) {
            return;
          }
          const { quota } = await this.store.fetchUserQuota(signal);
          return { quota };
        }).pipe(
          backoffRetry({
            when: isNetworkError,
            count: Infinity,
          }),
          backoffRetry({
            when: isBackendError,
          }),
          mergeMap(data => {
            if (data && typeof data === 'object' && 'quota' in data) {
              this.quota$.next((data as { quota: NonNullable<QuotaQuery['currentUser']>['quota'] }).quota);
            } else {
              this.quota$.next(null);
            }
            return EMPTY;
          }),
          catchErrorInto(this.error$),
          onStart(() => this.isRevalidating$.next(true)),
          onComplete(() => this.isRevalidating$.next(false))
        ),
      () => {
        this.reset();
      }
    )
  );

  reset() {
    this.quota$.next(null);
    this.error$.next(null);
    this.isRevalidating$.next(false);
  }

  dispose(): void {
    this.revalidate.unsubscribe();
  }
}
